import { useId } from 'react';

/**
 * Grupo de 2-4 opciones mutuamente excluyentes. Renderiza como radiogroup.
 *
 * Dos orientaciones:
 *  - 'horizontal' (default): segmentos contiguos en una línea, selected =
 *    bg-accent solid text-paper. Para opciones cortas.
 *  - 'vertical': opciones apiladas con radio dot a la izquierda + label +
 *    description opcional. Selected = border-brand bg-brand-tint. Para
 *    opciones que necesitan explicación de soporte.
 *
 * @param {Object} props
 * @param {Array<{ value: string, label: string, description?: string, disabled?: boolean }>} props.options
 *   2-4 opciones. `description` solo se renderiza en orientation='vertical'.
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal']
 * @param {'sm'|'md'} [props.size='md']
 * @param {string} [props.name]  Default: useId() autogenerado.
 * @param {string} [props.ariaLabel]
 */
export default function SegmentedControl({
  options,
  value,
  onChange,
  orientation = 'horizontal',
  size = 'md',
  name,
  ariaLabel,
}) {
  const reactId = useId();
  const groupName = name || reactId;

  if (process.env.NODE_ENV !== 'production') {
    if (options.length < 2 || options.length > 4) {
      // eslint-disable-next-line no-console
      console.warn(
        `[SegmentedControl] Recibiste ${options.length} options. El componente está pensado para 2-4. Para más opciones, usá <Field> con <select>.`
      );
    }
  }

  if (orientation === 'vertical') {
    return (
      <div role="radiogroup" aria-label={ariaLabel} className="flex flex-col">
        {options.map((opt, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === options.length - 1;
          const selected = value === opt.value;
          return (
            <VerticalItem
              key={opt.value}
              {...opt}
              name={groupName}
              size={size}
              selected={selected}
              isFirst={isFirst}
              isLast={isLast}
              onSelect={() => !opt.disabled && onChange(opt.value)}
            />
          );
        })}
      </div>
    );
  }

  // horizontal
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex items-stretch border border-edge rounded-tk-sm overflow-hidden"
    >
      {options.map((opt, idx) => {
        const selected = value === opt.value;
        const isLast = idx === options.length - 1;
        return (
          <HorizontalSegment
            key={opt.value}
            {...opt}
            name={groupName}
            size={size}
            selected={selected}
            isLast={isLast}
            onSelect={() => !opt.disabled && onChange(opt.value)}
          />
        );
      })}
    </div>
  );
}

function HorizontalSegment({ label, value, disabled, name, size, selected, isLast, onSelect }) {
  const sizeClass = size === 'sm' ? 'px-3 py-1.5 text-micro' : 'px-4 py-2 text-small';
  const stateClass = selected
    ? 'bg-[var(--color-accent)] text-paper'
    : 'bg-paper text-ink-muted hover:text-ink';
  const dividerClass = isLast ? '' : 'border-r border-edge';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      name={name}
      value={value}
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      className={[
        'font-sans font-medium transition-colors duration-150',
        sizeClass,
        stateClass,
        dividerClass,
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-inset',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function VerticalItem({ label, description, value, disabled, name, size, selected, isFirst, isLast, onSelect }) {
  const sizeClass = size === 'sm' ? 'px-3 py-2' : 'px-3.5 py-2.5';
  const labelClass = size === 'sm' ? 'text-micro' : 'text-small';
  const descClass = size === 'sm' ? 'text-10px' : 'text-11px';
  // border collapse vertical: el primero lleva su propio border-top, el
  // resto reutiliza el border-bottom del anterior. Implementamos con
  // border-bottom condicional + border-top solo en el primero.
  const borderShape = [
    'border border-edge',
    isFirst ? 'rounded-t-tk-sm' : '-mt-px',
    isLast ? 'rounded-b-tk-sm' : '',
  ].join(' ');
  const stateClass = selected
    ? 'border-brand bg-brand-tint relative z-10'
    : 'bg-paper hover:border-accent-border';

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      name={name}
      value={value}
      disabled={disabled}
      onClick={disabled ? undefined : onSelect}
      className={[
        'flex items-start gap-3 text-left font-sans transition-colors duration-150',
        sizeClass,
        borderShape,
        stateClass,
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:z-10',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      <RadioDot selected={selected} />
      <span className="flex-1 min-w-0">
        <span className={`block font-medium ${labelClass} ${selected ? 'text-brand font-semibold' : 'text-ink'}`}>
          {label}
        </span>
        {description && (
          <span className={`block ${descClass} text-ink-muted mt-0.5`}>
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

function RadioDot({ selected }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'flex-shrink-0 mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center',
        'transition-colors duration-150',
        selected ? 'border-brand' : 'border-ink-faint',
      ].join(' ')}
    >
      {selected && <span className="w-1.5 h-1.5 rounded-full bg-brand" />}
    </span>
  );
}
